import { MAX_NUMBER_OF_MARKERS_TO_DISPLAY_IN_SLIDER } from 'constants';
import { BookLocation } from 'types';
import {
  getCfiForPixelOffset,
  getDocumentForPercentageOfBook,
  getElementForCfi,
  getPageForPercentageOfBook,
  getPageForPercentageOfDocument,
  getPageForPercentageOfDocumentWithoutFrame,
} from 'utilities/locationLogic';

// Gets marks for slider based on the book's markers and max marks to display
export const getMarksForBook = (
  book,
  maxMarks = MAX_NUMBER_OF_MARKERS_TO_DISPLAY_IN_SLIDER,
) => {
  const markers = book?.topLevelMarkers ?? [];
  const marks = [];

  if (markers.length >= maxMarks) {
    return marks;
  }

  for (let marker of markers) {
    if (marker?.pages?.length) {
      const value = marker.pages[0].percentageStart * 10000;
      if (marks.find((mark) => mark.value === value)) {
        // no duplicate marks, skip it
        continue;
      } else {
        marks.push({ value });
      }
    }
  }

  return marks;
};

// Gets the new page for the percentage of the slider changing when slider thumb moves
export const getNewPageForPercentage = (book, newPercentage) => {
  const pageForPercentage =
    getPageForPercentageOfBook(book, newPercentage) ?? book.pages[0] ?? null;

  const newDocument =
    getDocumentForPercentageOfBook(book, newPercentage) ??
    book.documents[0] ??
    null;

  /**
   * the newPercentage set here will be used to calculate the BookLocation if user commits the sliderChange
   * BookLocation uses this percentage to calculate the document and resolves the location to exact page (needs documentFrame to resolve)
   * If there is no difference in characterCount between document and pages, their percentages would be similar, so
   * using getPageForPercentageOfBook would be more accurate (coz similar percentages)
   * If there is a difference then getting the document using the newPercentage then
   * using getPageForPercentageOfDocumentWithoutFrame (recalculates percentage for just the pages in the document) to get the page would be more accurate
   *  */
  if (
    newDocument &&
    pageForPercentage &&
    newDocument.pages.includes(pageForPercentage)
  ) {
    return pageForPercentage;
  }

  const percentageOfDocument = Boolean(newDocument.percentage)
    ? (newPercentage - newDocument.percentageStart) / newDocument.percentage
    : 0;

  return getPageForPercentageOfDocumentWithoutFrame(
    newDocument,
    percentageOfDocument,
  );
};

// Finds the next valid element sibling that is not blacklisted and has text content
// This is used to generate CFI for pagebreak elements since they are in the CFI blacklist
// Empty elements like <hr/> don't work well with CFI generation
const getNextValidSibling = (element) => {
  let sibling = element.nextElementSibling;
  while (sibling) {
    const isBlacklisted = sibling.classList.contains('rs-auto-pagebreak');
    const hasTextContent =
      sibling.textContent && sibling.textContent.trim().length > 0;

    if (!isBlacklisted && hasTextContent) {
      return sibling;
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
};

// This exists coz in some books, element that determine page breaks is embedded in other elements
// because of that generating cfi from element and vice-versa doesn't always give same result (cfiLogic.getElementForCfi)
// so, we end up landing on wrong page than expected
export const getStablePageLocation = (frame, page, currentBookLocation) => {
  const frameParent = frame.parentElement.parentElement;
  const comparedToHeight = frameParent.scrollHeight - frameParent.offsetHeight;

  const elementForPage = frame.contentDocument.getElementById(page.element);

  // If there is no pagebreak element in frame or comparedToHeight is 0, use default
  if (!elementForPage || !comparedToHeight) {
    return page.bookLocation;
  }

  // For rs-auto-pagebreak elements, use the next sibling element for CFI generation
  // because pagebreak elements are in the CFI blacklist and produce invalid CFIs
  let targetElement = elementForPage;
  if (elementForPage.classList.contains('rs-auto-pagebreak')) {
    const nextSibling = getNextValidSibling(elementForPage);
    if (nextSibling) {
      targetElement = nextSibling;
    }
  }

  let elementPixel = targetElement.getBoundingClientRect().top;

  let cfiForPageElement;
  let pageIndexGreaterThanRequired = false;
  let pageIndexLessThanRequired = false;
  let adjustment = 10;

  while (elementForPage) {
    // using the page element's pixel offset to get the cfi and
    // use generated cfi to see if we land on same page

    cfiForPageElement = getCfiForPixelOffset(frame, elementPixel);

    const elementForCfi = getElementForCfi(frame, cfiForPageElement);

    const percentageOfDocumentForCfi =
      elementForCfi.getBoundingClientRect().top / comparedToHeight;

    // if the percentage is greater than 1, then the element is at the end of the document
    // we dont need to check for less than 0, coz getCfiForPixelOffset will get top if that happens
    if (percentageOfDocumentForCfi > 1) {
      return new BookLocation({
        book: currentBookLocation.book,
        document: currentBookLocation.document,
        percentageOfDocument: 1,
      });
    }

    const pageForCfi = getPageForPercentageOfDocument(
      frame,
      page.document,
      percentageOfDocumentForCfi,
    );

    // if the new page is the same as the required page, return the location
    // else move some pixels up or down based on the page index to get the correct page
    if (page.index === pageForCfi.index) {
      break;
    } else if (pageForCfi.index > page.index) {
      elementPixel = elementForCfi.getBoundingClientRect().top - adjustment;
      pageIndexGreaterThanRequired = true;
    } else {
      elementPixel = elementForCfi.getBoundingClientRect().bottom + adjustment;
      pageIndexLessThanRequired = true;
    }

    // if we are oscillating between the pages before and after the required page,
    // then there is no exact element for the page so return default
    if (pageIndexGreaterThanRequired && pageIndexLessThanRequired) {
      return page.bookLocation;
    }

    // increase adjustment to avoid infinite loop
    adjustment += 10;
    if (adjustment > 1000) {
      return page.bookLocation;
    }
  }

  // return location based on cfi to get to page
  return new BookLocation({
    book: currentBookLocation.book,
    document: currentBookLocation.document,
    cfi: cfiForPageElement,
  });
};
